import {
  LayoutDashboard,
  Users,
  Building2,
  BarChart3,
  ShieldAlert,
  ServerCog,
  FileText,
  ListChecks,
  MessageCircle,
  Settings,
  LogOut,
  HeartHandshake,
  HelpCircle,
  BookOpen,
  Shield,
} from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router'
import { cn } from '@/lib/utils'
import eyeLogo from '@/assets/Landing/eyelogo.svg'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName } from '@/utils/user-display'

interface AdminSidebarProps {
  className?: string
}

const menuItems = [
  { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
  { name: 'Users', path: '/admin/users', icon: Users },
  { name: 'Couples', path: '/admin/couples', icon: HeartHandshake },
  { name: 'Organizations', path: '/admin/organizations', icon: Building2 },
  { name: 'Analytics', path: '/admin/analytics', icon: BarChart3 },
  { name: 'Learning Path', path: '/admin/learning-path', icon: BookOpen },
  { name: 'Learning', path: '/admin/learning', icon: ListChecks },
  { name: 'Questions', path: '/admin/questions', icon: HelpCircle },
  { name: 'System', path: '/admin/system', icon: ServerCog },
  { name: 'Reports', path: '/admin/reports', icon: FileText },
  { name: 'Waitlist', path: '/admin/waitlist', icon: ShieldAlert },
  { name: 'Content', path: '/admin/content', icon: MessageCircle },
  { name: 'Settings', path: '/admin/settings', icon: Settings },
]

function isItemActive(pathname: string, itemPath: string): boolean {
  // Exact match
  if (pathname === itemPath) return true
  // Match sub-routes (e.g. /admin/users/123) but NOT prefix collisions
  // e.g. /admin/learning should NOT match /admin/learning-path
  if (pathname.startsWith(itemPath + '/')) return true
  return false
}

export function AdminSidebarContent() {
  const location = useLocation()
  const navigate = useNavigate()
  const { logout, user } = useAuth()
  const displayName = getUserDisplayName(user)

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className='flex flex-col h-full bg-[#F8F9FA]'>
      {/* Logo */}
      <div className='px-6 py-6'>
        <div className='flex items-center gap-2'>
          <img src={eyeLogo} alt='IRIS Logo' className='h-8 w-8' />
          <span className='text-[24px] font-bold text-[#151515]'>IRIS</span>
        </div>
        <p className='text-[10px] text-[#4e4d4d] mt-1'>
          Intimate Relationship Indicators Screening
        </p>
      </div>

      {/* Admin Panel Card */}
      <div className='mx-4 mb-6'>
        <div className='flex items-center gap-3 rounded-[12px] bg-white px-4 py-4'>
          <div className='h-10 w-10 rounded-full bg-[#151515] flex items-center justify-center flex-shrink-0'>
            <Shield className='h-5 w-5 text-[#FA6017]' />
          </div>
          <div>
            <p className='text-[14px] font-semibold text-[#151515] leading-tight'>
              Admin Panel
            </p>
            {displayName && (
              <p className='text-[11px] text-[#4e4d4d] mt-0.5'>
                {displayName}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className='flex-1 px-2 overflow-y-auto'>
        {menuItems.map((item) => {
          const isActive = isItemActive(location.pathname, item.path)
          const Icon = item.icon
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'relative flex items-center gap-3 px-4 py-3 text-[14px] font-medium transition-colors',
                isActive
                  ? 'text-[#FA6017]'
                  : 'text-[#4e4d4d] hover:text-[#151515]'
              )}
            >
              {/* Active indicator - orange bar on left edge */}
              {isActive && (
                <div className='absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[24px] bg-[#FA6017] rounded-r-full' />
              )}

              <Icon
                className={cn(
                  'h-5 w-5 shrink-0',
                  isActive ? 'text-[#FA6017]' : 'text-[#4e4d4d]'
                )}
              />
              <span>{item.name}</span>
            </Link>
          )
        })}
      </nav>

      {/* Logout Button */}
      <div className='px-4 py-4 border-t border-[#E5E7EB]'>
        <button
          onClick={handleLogout}
          className='w-full flex items-center gap-3 px-4 py-3 text-[14px] font-medium text-[#EF4444] hover:bg-red-50 rounded-lg transition-colors'
        >
          <LogOut className='h-5 w-5' />
          <span>Logout</span>
        </button>
      </div>
    </div>
  )
}

export function AdminSidebar({ className }: AdminSidebarProps) {
  return (
    <aside className={cn('hidden h-screen w-[300px] lg:block border-r border-gray-200 bg-white', className)}>
      <AdminSidebarContent />
    </aside>
  )
}

export default AdminSidebar
